import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "../ReusableComponents/supabaseClient";
import { shareLink } from "../Utilities/shareUtils";
import PoolRulesPopover from "../ReusableComponents/PoolRulesPopover";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const getScoreClass = (score) => {
  if (!score || score === "--" || score === "E") return "text-gray-900";
  const num = parseInt(score);
  if (isNaN(num)) return "text-gray-900";
  return num < 0 ? "text-red-600" : num > 0 ? "text-blue-600" : "text-gray-900";
};

const formatScore = (score) => {
  if (!score || score === "--") return "--";
  if (score === "E" || score === "0") return "E";
  const num = parseInt(score);
  if (isNaN(num)) return score;
  return num > 0 ? `+${num}` : `${num}`;
};

const abbreviateName = (name) => {
  if (!name) return "Empty";
  const parts = name.trim().split(/\s+/);
  if (parts.length <= 1) return name;
  return `${parts[0][0]}. ${parts.slice(1).join(" ")}`;
};

const formatPlayerStatus = (status, holesPlayed, teeTime) => {
  const s = status?.toLowerCase();
  if (s === "active") {
    return holesPlayed && holesPlayed !== "--" ? `Thru ${holesPlayed}` : "Active";
  }
  if (s === "not started") {
    return teeTime
      ? new Date(teeTime).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
      : "--";
  }
  if (s === "complete") {
    if (teeTime && new Date(teeTime) > new Date()) {
      return new Date(teeTime).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    }
    return "F";
  }
  return status || "--";
};

const PublicLeaderboardPage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedTeam, setExpandedTeam] = useState(null);
  const poolIdRef = useRef(null);

  const fetchLeaderboard = useCallback(async () => {
    try {
      const response = await fetch(`/api/leaderboard/getPublicLeaderboard?token=${token}`);
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.details || (response.status === 404 ? "Leaderboard not found." : "Failed to load leaderboard."));
      }
      const result = await response.json();
      setData(result);
      poolIdRef.current = result.pool?.id;
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Initial fetch + fallback polling
  useEffect(() => {
    fetchLeaderboard();
    const interval = setInterval(fetchLeaderboard, 60000);
    return () => clearInterval(interval);
  }, [fetchLeaderboard]);

  // Real-time subscriptions once we know the pool ID
  useEffect(() => {
    if (!poolIdRef.current) return;
    const poolId = poolIdRef.current;

    const teamChannel = supabase
      .channel(`public-lb-team-${poolId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "team", filter: `pool=eq.${poolId}` }, () => {
        fetchLeaderboard();
      })
      .subscribe();

    const poolChannel = supabase
      .channel(`public-lb-pool-${poolId}`)
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "pool", filter: `id=eq.${poolId}` }, () => {
        fetchLeaderboard();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(teamChannel);
      supabase.removeChannel(poolChannel);
    };
  }, [data?.pool?.id, fetchLeaderboard]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-10 w-10 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-500">Loading leaderboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center border border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Unable to Load</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  const { pool, teams } = data;
  const tournamentStarted = pool.tournamentStatus && pool.tournamentStatus !== "Not Started";

  const handleShare = async () => {
    const result = await shareLink(window.location.href, `${pool.name} Leaderboard`, "Check out this golf pool leaderboard!");
    if (result === "copied") toast.success("Leaderboard link copied!", { theme: "dark", autoClose: 2000 });
    else if (result === "shared") toast.success("Link shared!", { theme: "dark", autoClose: 2000 });
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <ToastContainer position="top-center" theme="dark" />
      {/* Header */}
      <div className="bg-gradient-to-r from-primary to-primary-light text-white">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <p className="text-green-200 text-sm font-medium uppercase tracking-wider">GolfPools Leaderboard</p>
          <div className="flex items-center justify-between mt-1">
            <h1 className="text-2xl font-bold min-w-0 truncate">{pool.name}</h1>
            <PoolRulesPopover
              maxPlayers={pool.maxPlayers}
              numPlayersScored={pool.numPlayersScored}
              allMustMakeCut={pool.allMustMakeCut}
              tieBreaker={pool.tieBreaker}
              onCopyLink={handleShare}
            />
          </div>
          {pool.tournamentName && pool.tournamentName !== pool.name && (
            <p className="text-green-200 mt-1">{pool.tournamentName}</p>
          )}

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3 mt-4">
            {[
              { label: "Status", value: pool.tournamentStatus || "Not Started" },
              { label: "Round", value: pool.currentRound || "--" },
              { label: "Round Status", value: pool.roundStatus || "--" },
              { label: "Cut", value: pool.cutValue || "--" },
            ].map((stat) => (
              <div key={stat.label} className="bg-white/15 backdrop-blur-sm rounded-xl p-3 text-center">
                <p className="text-[10px] font-semibold text-green-200 uppercase tracking-wider">{stat.label}</p>
                <p className="text-sm font-bold text-white mt-1">{stat.value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Leaderboard */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        {teams.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-12 text-center">
            <p className="text-gray-400 text-lg">No teams have joined this pool yet.</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            {!tournamentStarted && (
              <div className="px-5 py-3 bg-amber-50 border-b border-amber-100 text-center">
                <p className="text-amber-700 text-sm">Tournament hasn't started yet. Team details will be visible once play begins.</p>
              </div>
            )}

            <div className="divide-y divide-gray-100">
              {teams.map((team, index) => {
                const isExpanded = expandedTeam === team.id;
                const isWinner = team.rank === "1" && pool.tournamentStatus === "Official";
                const isCut = team.rank === "MC";
                const hasScore = team.team_score_to_par && team.team_score_to_par !== "--" && team.team_score_to_par !== "E" && team.team_score_to_par !== null;
                const canExpand = tournamentStarted && team.line_items?.length > 0;

                return (
                  <div key={team.id}>
                    {/* Team Row */}
                    <div
                      className={`flex items-center justify-between px-5 py-4 ${canExpand ? "cursor-pointer hover:bg-gray-50" : ""} transition-colors`}
                      onClick={() => canExpand && setExpandedTeam(isExpanded ? null : team.id)}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                          isWinner ? "bg-yellow-100 text-yellow-700" :
                          isCut ? "bg-red-100 text-red-600" :
                          "bg-gray-100 text-gray-600"
                        }`}>
                          {isCut ? "MC" : team.rank || (index + 1)}
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900 flex items-center gap-1.5 text-sm">
                            {team.player_name || "Unknown"}
                            {isWinner && <span className="text-yellow-500">🏆</span>}
                          </p>
                          <p className="text-xs text-gray-500">
                            {team.line_items?.length || 0}/{pool.maxPlayers} players
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        {(hasScore || tournamentStarted) && (
                          <p className={`text-lg font-bold ${getScoreClass(team.team_score_to_par)}`}>
                            {formatScore(team.team_score_to_par)}
                          </p>
                        )}
                        {canExpand && (
                          <svg className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        )}
                      </div>
                    </div>

                    {/* Expanded Player Details */}
                    {isExpanded && canExpand && (
                      <div className="border-t border-gray-100 bg-gray-50">
                        <table className="w-full table-fixed">
                          <colgroup>
                            <col style={{ width: "8%" }} />
                            <col style={{ width: "40%" }} />
                            <col style={{ width: "16%" }} />
                            <col style={{ width: "18%" }} />
                            <col style={{ width: "18%" }} />
                          </colgroup>
                          <thead>
                            <tr className="border-b border-gray-100">
                              {["#", "Player", "Pos", "To Par", "Status"].map((h) => (
                                <th key={h} className={`text-[10px] font-semibold text-gray-400 uppercase tracking-wider py-2 px-1.5 ${h === "Player" ? "text-left" : "text-center"}`}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {[...team.line_items].sort((a, b) => {
                              const statusOrder = (s) => {
                                if (!s || s.toLowerCase() === "not started") return 2;
                                if (["cut", "wd", "dq"].includes(s.toLowerCase())) return 1;
                                return 0;
                              };
                              const oa = statusOrder(a.players_tournament_status);
                              const ob = statusOrder(b.players_tournament_status);
                              if (oa !== ob) return oa - ob;
                              const scoreToNum = (s) => {
                                if (!s || s === "--" || s === "-") return Infinity;
                                if (s === "E") return 0;
                                return parseInt(s, 10) || Infinity;
                              };
                              return scoreToNum(a.total_score_to_par) - scoreToNum(b.total_score_to_par);
                            }).map((item, i) => (
                              <tr key={i} className={`text-sm border-b border-gray-100 last:border-0 ${i % 2 === 1 ? "bg-gray-50/50" : ""}`}>
                                <td className="text-center text-gray-400 text-xs py-2.5 px-1.5">{i + 1}</td>
                                <td className="font-medium text-gray-900 truncate py-2.5 px-1.5 text-xs">
                                  <span className="hidden md:inline">{item.player?.player_name || "Empty"}</span>
                                  <span className="md:hidden">{abbreviateName(item.player?.player_name)}</span>
                                  {item.player?.world_ranking_position && (
                                    <span className="text-gray-400 hidden md:inline"> #{item.player.world_ranking_position}</span>
                                  )}
                                </td>
                                <td className="text-center text-xs text-gray-600 font-medium py-2.5 px-1.5">{item.tournament_position || "--"}</td>
                                <td className={`text-center text-xs font-semibold py-2.5 px-1.5 ${getScoreClass(item.total_score_to_par)}`}>{formatScore(item.total_score_to_par)}</td>
                                <td className="text-center text-xs text-gray-500 py-2.5 px-1.5 whitespace-nowrap">{formatPlayerStatus(item.players_tournament_status, item.holes_played, item.tee_time)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default PublicLeaderboardPage;
